import { Component } from 'preact';
import {
  TEXT_PRIMARY, GOLD_BTN,
  TAB_ACTIVE_TEXT, TAB_INACTIVE, TAB_BAR_STYLE,
} from '../../lib/medievalTheme';
import { TabBar } from '../ui/TabBar';
import { GAME_MODES, stakesLabel } from '../../utils/arena/gameModes';
import { TIERS, TIER_LABELS } from '../../utils/arena/rankUtils';
import { Tooltip } from '../ui/tooltip';
import MatchupDisplay from './MatchupDisplay';
import { t } from '../../localization/t';

// Smart-match band around the user's current tier. Mirrors the
// "match within ±1 tier" heuristic used by ladder games like League
// of Legends, scaled down to our six-tier ladder. Edge tiers clamp:
// an Apprentice never sees a phantom tier below them.
const RECOMMENDED_RADIUS = 1;

function getRecommendedTiers(userTier) {
  if (!userTier) return null;
  const idx = TIERS.indexOf(userTier);
  if (idx < 0) return null;
  const lo = Math.max(0, idx - RECOMMENDED_RADIUS);
  const hi = Math.min(TIERS.length - 1, idx + RECOMMENDED_RADIUS);
  return TIERS.slice(lo, hi + 1);
}

const RECOMMENDED_TAB = { value: 'recommended', label: 'Recommended' };
const ALL_TAB = { value: 'all', label: 'All' };
const TIER_TABS = TIERS.map((t) => ({ value: t, label: TIER_LABELS[t] }));

function modeTitle(key) {
  return GAME_MODES.find((m) => m.key === key)?.title || key;
}

export function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMin = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (diffMin < 1) return t('just now');
  if (diffMin < 60) return t('{n}m ago', { n: diffMin });
  const hrs = Math.round(diffMin / 60);
  if (hrs < 24) return t('{n}h ago', { n: hrs });
  const days = Math.round(hrs / 24);
  return t('{n}d ago', { n: days });
}

// Hosts may come from a stale client that still reads creator_tier off
// the row instead of the participants array. Prefer the participant
// payload so all four players in a 4-seat lobby share the same code
// path, but fall back to the row-level creator_* fields when the
// participants entry is missing those fields (e.g. an older server).
function ensureHostInParticipants(inst) {
  const list = inst.participants || [];
  if (list.length === 0) return list;
  const hostIdx = list.findIndex((p) => p.role === 'creator');
  if (hostIdx < 0) return list;
  const host = list[hostIdx];
  if (host.name) return list;
  const hydrated = {
    ...host,
    name: inst.creator_name || host.name,
    tier: host.tier ?? inst.creator_tier,
    division: host.division ?? inst.creator_division,
    xp: host.xp ?? inst.creator_xp,
    discordHandle: host.discordHandle ?? inst.creator_discord_handle,
  };
  const copy = [...list];
  copy[hostIdx] = hydrated;
  return copy;
}

export default class PublicBrowser extends Component {
  constructor(props) {
    super(props);
    const recommendedTiers = getRecommendedTiers(props.currentUserTier);
    this.state = {
      rankFilter: recommendedTiers ? 'recommended' : 'all',
    };
  }

  setRankFilter = (value) => this.setState({ rankFilter: value });

  render() {
    const {
      instances, currentUserId, currentUserTier, sorceryCards, onJoin,
    } = this.props;
    const { rankFilter } = this.state;

    const recommendedTiers = getRecommendedTiers(currentUserTier);
    const recommendedSet = recommendedTiers ? new Set(recommendedTiers) : null;
    const tabs = [
      ...(recommendedTiers ? [{ ...RECOMMENDED_TAB, label: t('Recommended') }] : []),
      { ...ALL_TAB, label: t('All') },
      ...TIER_TABS,
    ];

    const list = instances || [];
    const tabCount = (value) => {
      if (value === 'all') return list.length;
      if (value === 'recommended') {
        return recommendedSet ? list.filter((i) => recommendedSet.has(i.creator_tier)).length : list.length;
      }
      return list.filter((i) => i.creator_tier === value).length;
    };

    const matchesFilter = (inst) => {
      if (rankFilter === 'all') return true;
      if (rankFilter === 'recommended') {
        return recommendedSet ? recommendedSet.has(inst.creator_tier) : true;
      }
      return inst.creator_tier === rankFilter;
    };

    const tabBar = (
      <TabBar
        role="tablist"
        aria-label={t('Filter public games by rank')}
        className="flex items-center mb-2 overflow-x-auto"
        style={{ ...TAB_BAR_STYLE, gap: '0.125cqw' }}
        activeIndex={tabs.findIndex((t) => t.value === rankFilter)}
      >
        {tabs.map((tab) => {
          const active = rankFilter === tab.value;
          const isRecommended = tab.value === 'recommended';
          const count = tabCount(tab.value);
          const button = (
            <button
              key={tab.value}
              type="button"
              role="tab"
              data-tab-item
              aria-selected={active}
              className="flex-1 min-w-0 px-2 py-1 text-[0.6875cqw] font-semibold cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-1"
              style={active ? TAB_ACTIVE_TEXT : TAB_INACTIVE}
              onClick={() => this.setRankFilter(tab.value)}
            >
              {isRecommended ? <span aria-hidden="true">✦</span> : null}
              {t(tab.label)}
              {count > 0 ? (
                <span
                  className="tabular-nums"
                  style={{ color: active ? '#f0dca8' : 'rgba(166, 160, 155, 0.7)' }}
                >
                  {count}
                </span>
              ) : null}
            </button>
          );
          if (isRecommended && recommendedTiers) {
            const range = recommendedTiers.map((tier) => TIER_LABELS[tier]).join(' · ');
            return (
              <Tooltip
                key={tab.value}
                delay={250}
                side="bottom"
                maxWidth="22cqw"
                content={(
                  <div className="flex flex-col gap-1">
                    <div className="font-bold" style={{ color: '#f4d788' }}>
                      ✦ {t('Recommended')}
                    </div>
                    <div className="font-normal" style={{ opacity: 0.85 }}>
                      {t('Hosts within {n} tier of {tier}', { n: RECOMMENDED_RADIUS, tier: TIER_LABELS[currentUserTier] })}
                    </div>
                    <div className="font-semibold" style={{ opacity: 0.7 }}>
                      {range}
                    </div>
                  </div>
                )}
              >
                {button}
              </Tooltip>
            );
          }
          return button;
        })}
      </TabBar>
    );

    const filtered = (instances || []).filter(matchesFilter);

    if (!instances?.length) {
      return (
        <div className="flex flex-col">
          {tabBar}
          <div className="vh-empty-state">
            <div className="vh-empty-state-headline">{t('No public games open')}</div>
            <p className="vh-empty-state-hint">
              {t('Nobody is hosting a public match right now. Start one on the left to open the gates for anyone to join.')}
            </p>
          </div>
        </div>
      );
    }

    if (!filtered.length) {
      const isRecommended = rankFilter === 'recommended';
      const tabLabel = tabs.find((t) => t.value === rankFilter)?.label || rankFilter;
      return (
        <div className="flex flex-col">
          {tabBar}
          <div className="vh-empty-state">
            <div className="vh-empty-state-headline">
              {isRecommended ? t('No games in your range right now') : t('No {tier} games right now', { tier: tabLabel })}
            </div>
            <p className="vh-empty-state-hint">
              {isRecommended
                ? t('Nobody at a similar rank is hosting. Switch to All to see every public match.')
                : t('No public matches hosted by a {tier} player. Clear the filter to see games at every rank.', { tier: tabLabel })}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col">
        {tabBar}
        <div className="flex flex-col gap-2">
          {filtered.map((inst) => {
            const participants = ensureHostInParticipants(inst);
            const seatCount = Number(
              inst.seat_count ?? participants.length ?? 0,
            );
            const alreadyIn = participants.some((p) => p.userId === currentUserId);
            const full = seatCount >= inst.seats_max;
            return (
              <div key={inst.id} className="vh-save-row">
                <div className="flex flex-col gap-2 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span
                      className="text-sm font-semibold arena-heading"
                      style={{
                        color: TEXT_PRIMARY,
                        textShadow: '0 -1px 0 rgba(0,0,0,0.6), 0 1px 2px rgba(0,0,0,0.5)',
                      }}
                    >
                      {t(modeTitle(inst.mode))}
                      {stakesLabel(inst.mode, inst.ranked_opt_in) ? ` · ${t(stakesLabel(inst.mode, inst.ranked_opt_in))}` : ''}
                    </span>
                    {inst.created_at ? (
                      <span className="text-[0.6875cqw] opacity-60">
                        {t('opened {when}', { when: timeAgo(inst.created_at) })}
                      </span>
                    ) : null}
                  </div>
                  <MatchupDisplay
                    participants={participants}
                    seatsMax={inst.seats_max}
                    creatorId={inst.creator_id}
                    sorceryCards={sorceryCards}
                  />
                </div>
                <button
                  type="button"
                  className="px-5 py-2 text-xs font-semibold cursor-pointer shrink-0"
                  style={{ ...GOLD_BTN, opacity: (alreadyIn || full) ? 0.5 : 1 }}
                  disabled={alreadyIn || full}
                  onClick={() => onJoin?.(inst)}
                >
                  {alreadyIn ? t('Already In') : full ? t('Full') : t('Join')}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}
